<template>
  <mf-card-container>
    <template v-slot:title-complement>
      <span class="title d-flex align-center">
        Configuração SMS
        <v-icon class="ml-2 channel-icon">mdi-message-reply-text</v-icon>
      </span>
    </template>

    <template v-slot:headers>
      <div class="d-flex" style="gap: 16px">
        <best-offer-configuration-dialog channel="sms" :offer-type="selectedOfferType" @updated-variables="checkAllCharacters" />
        <template-message-modal v-model="selectedTemplate" :mode="['SMS']" />
      </div>
    </template>

    <div class="message-input-container mt-6">
      <div class="d-flex flex-row" style="gap: 14px">
        <div class="d-flex flex-column flex-50">
          <span class="lato-subtitulo">Mensagem</span>
        </div>

        <div class="d-flex flex-column flex-50">
          <div class="d-flex flex-row justify-space-between">
            <span class="lato-subtitulo">Inspeção</span>
            <div class="d-flex flex-row align-center" style="gap: 4px">
              <mf-icon size="20" fill :color="consumedCreditColor" :icon="consumedCreditIcon" />
              <span class="credit-text" :class="consumedCredits <= 1 ? 'verde-0-text' : 'vermelho-d20-text'">{{ consumedCreditsText }}</span>
            </div>
          </div>
          <div class="d-flex flex-row justify-space-between">
            <offer-type-options-menu :selected-offer-type.sync="selectedOfferType" :invalid-offer-types="invalidOfferTypes" :items="offerTypeOptions" />
            <span class="characters-count" :class="{ 'vermelho-d20-text': consumedCredits > 1 }"
              >{{ charCountByOfferType[selectedOfferType] }}/{{ charPerCredit }}</span
            >
          </div>
        </div>
      </div>

      <div class="d-flex flex-row" style="gap: 14px">
        <div class="d-flex flex-column flex-50">
          <input-with-best-offer
            v-model="message"
            channel="sms"
            field="text"
            :offer-type="selectedOfferType"
            :char-count="charCountByOfferType[selectedOfferType]"
            :error="inputErrorMessage.length > 0"
            :error-message="inputErrorMessage"
          />
        </div>

        <div class="d-flex flex-column flex-50">
          <sms-preview :message="message" :offer-type="selectedOfferType" channel="sms" insert-best-offer-as-div />
        </div>
      </div>
    </div>
  </mf-card-container>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { channelsForm } from '@/modules/maria/campanha-da-maria-v2/mixins/channels-form'
import { charCount } from '@/modules/maria/campanha-da-maria-v2/mixins/char-count'
import { notOnlySpace } from '@/helpers/validators.js'
import { bestOfferValidations } from '@/modules/maria/campanha-da-maria-v2/mixins/best-offer-validations'

export default {
  name: 'SmsForm',
  components: {
    BestOfferConfigurationDialog: () =>
      import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/BestOfferConfiguration/BestOfferConfigurationDialog.vue'),
    TemplateMessageModal: () => import('@/components/template-message/TemplateMessageModal.vue'),
    InputWithBestOffer: () => import('../InputWithBestOffer.vue'),
    SmsPreview: () => import('./SmsPreview.vue'),
  },
  mixins: [channelsForm, charCount, bestOfferValidations],
  data: () => ({
    message: '',
    selectedTemplate: {},
    inputErrorMessage: [],
    charCountByOfferType: {},
    hasSpecialCharactersByOfferType: {},
  }),
  computed: {
    channelSettings() {
      return this.campaignSettings.channels.sms || {}
    },
    invalidOfferTypes() {
      const invalidOfferTypes = []

      this.offerTypeOptions.forEach((option) => {
        const hasSpecialChars = this.hasSpecialCharactersByOfferType[option.value]
        const charCount = this.charCountByOfferType[option.value]
        const charsPerCredit = hasSpecialChars ? 70 : 160
        const creditsConsumed = Math.ceil(charCount / charsPerCredit)

        if (creditsConsumed > 1) {
          invalidOfferTypes.push(option.value)
        }
      })

      return invalidOfferTypes
    },
    consumedCreditIcon() {
      if (this.consumedCredits <= 1) return 'check_circle'
      else return 'warning'
    },
    consumedCreditColor() {
      if (this.consumedCredits <= 1) return 'verde-l20'
      else return 'vermelho-0'
    },
    consumedCreditsText() {
      const credits = Math.ceil(this.charCountByOfferType[this.selectedOfferType] / this.charPerCredit)
      if (credits <= 1) return '1 crédito de consumo'
      else return `${credits} créditos de consumo`
    },
    consumedCredits() {
      return Math.ceil(this.charCountByOfferType[this.selectedOfferType] / this.charPerCredit)
    },
    charPerCredit() {
      return this.hasSpecialCharactersByOfferType[this.selectedOfferType] ? 70 : 160
    },
  },
  watch: {
    selectedTemplate(newValue) {
      if (newValue.message !== this.message) {
        this.message = newValue.message
      }
    },
    message(newValue) {
      this.clearInputErrorMessage()
      this.checkAllCharacters()
    },
  },
  mounted() {
    this.message = this.channelSettings.message?.text || ''
    this.createCharCount()
    this.createHasSpecialCharacters()
    this.checkAllCharacters()
  },
  methods: {
    clearInputErrorMessage() {
      this.inputErrorMessage = []
    },
    createCharCount() {
      const charCountByOfferType = {}
      this.offerTypeOptions.forEach((option) => {
        charCountByOfferType[option.value] = 0
      })
      this.$set(this, 'charCountByOfferType', charCountByOfferType)
    },
    createHasSpecialCharacters() {
      const hasSpecialCharactersByOfferType = {}
      this.offerTypeOptions.forEach((option) => {
        hasSpecialCharactersByOfferType[option.value] = false
      })
      this.$set(this, 'hasSpecialCharactersByOfferType', hasSpecialCharactersByOfferType)
    },
    checkAllCharacters() {
      this.offerTypeOptions.forEach((option) => {
        this.charCountByOfferType[option.value] = this.countTextWithVariables(this.message, 'sms', 'text', option.value)
        this.hasSpecialCharactersByOfferType[option.value] = this.hasSpecialCharactersInText(this.message)
      })
    },
    validInput() {
      this.clearInputErrorMessage()

      this.inputErrorMessage.push(...this.validateVariables(this.message))

      if (this.hasMoreThanOneBestOffer(this.message)) {
        this.inputErrorMessage.push('A mensagem não pode conter mais de uma variável de melhor oferta.')
      }

      if (this.hasInvalidBestOffer(this.message)) {
        this.inputErrorMessage.push('A mensagem contém uma variável de melhor oferta inválida.')
      }

      if (!this.hasEnoughProducts(this.message)) {
        this.inputErrorMessage.push(
          'Para utilizar essa quantidade de produtos na variável de melhor oferta, você precisa ter pelo menos essa quantidade de produtos no passo 2. Por favor volte e insira os produtos faltantes.'
        )
      }

      if (!this.isPricesCorrect('sms', 'text', 'offer_sales', this.message)) {
        this.inputErrorMessage.push(
          'Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2. Por favor volte e preencha os campos deixados em branco ou remova os preços das suas mensagens.'
        )
      }

      if (!this.$v.message.required) {
        this.inputErrorMessage.push('A mensagem não pode ser vazia.')
      }

      if (!this.$v.message.notOnlySpace) {
        this.inputErrorMessage.push('Não é permitido salvar apenas espaços.')
      }

      return this.inputErrorMessage.length === 0
    },
    getChannelData() {
      return {
        ...this.channelSettings,
        message: {
          text: this.message,
          amount_best_offers: null,
        },
      }
    },
  },
  validations() {
    return {
      message: {
        required,
        notOnlySpace,
      },
    }
  },
}
</script>

<style scoped lang="scss">
.message-input-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-radius: 8px;
  padding: 24px 20px;
  background: $cinza-l90;
}

.flex-50 {
  flex: 50%;
}

.lato-subtitulo {
  font-weight: bold;
  font-size: 16px;
  color: $cinza-0;
}

.credit-text {
  font-weight: bold;
  font-size: 14px;
}

.verde-0-text {
  color: $verde-0;
}

.vermelho-d20-text {
  color: $vermelho-d20 !important;
}

.characters-count {
  font-size: 14px;
  color: #656565;
}
</style>
